import React, { useState, useEffect } from 'react'
import noop from 'lodash/noop'
import { Button, TextInputOld, Dropdown, Row, Col, IDropdownItem } from '@doseme/cohesive-ui'
import { isLoaded } from 'flux-entities'

import { TProps } from './types'
import { PLANS_URL } from '../../../../constants/urls/frontend'
import {
  doseMe,
  trial,
  customer,
  research,
  TPlanType,
  ISelectCountHashmap,
  INewPlan,
  IPlanDrug,
  tdi
} from '../../../../types/plan'
import { FormPageLayout } from '../../../../components/FormPageLayout'
import { StyledForm } from '../../../../components/StyledForm'
import { TFormsValidity } from '../../types'
import { EntitlementsInput } from '../../../../components/EntitlementsInput'
import { IPlanForm } from '../types'

type TKey = 'name' | 'description' | 'type'

const Create: React.FC<TProps> = ({ plansLoading, drugs, fetchDrugs, createPlan, history }) => {
  const [formPlan, update] = useState<IPlanForm>({
    description: '',
    drugs: {},
    type: 'DoseMe',
    name: ''
  })

  const [fieldsValidity, setFieldsValidity] = useState<TFormsValidity<TKey>>({
    name: {
      touched: false,
      valid: true
    },
    description: {
      touched: true,
      valid: true
    },
    type: {
      touched: false,
      valid: true
    }
  })

  const [error, updateError] = useState('')

  useEffect(() => {
    fetchDrugs()
  }, [fetchDrugs])

  useEffect(() => {
    if (drugs.ids.length) {
      const fill: ISelectCountHashmap = {}
      for (const x of Object.keys(drugs.all)) {
        fill[x] = { selected: false, count: 0 }
      }
      // can't make reference to formPlan in this useEffect as it causes infinite loop
      // so we are just specifying the required properties manually instead
      update({
        description: '',
        type: 'DoseMe',
        name: '',
        drugs: fill
      })
    }
  }, [drugs.all, drugs.ids.length, fetchDrugs, update])

  if (!isLoaded(drugs)) {
    return <div>Loading...</div>
  }

  const setSelectedDrugs = (entitlements: ISelectCountHashmap) => {
    update({
      ...formPlan,
      drugs: entitlements
    })
  }

  const handleInput = (name: string, value: string, valid: boolean) => {
    if (valid) {
      update({ ...formPlan, [name]: value })
    }

    setFieldsValidity({
      ...fieldsValidity,
      [name]: {
        touched: true,
        valid
      }
    })
  }

  const handleSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    updateError('')

    const formattedDrugs: IPlanDrug[] = []
    for (const drug of Object.keys(formPlan.drugs)) {
      if (formPlan.drugs[drug].count !== 0) {
        formattedDrugs.push({
          id: drugs.all[drug].id,
          maxCourseCount: formPlan.drugs[drug].count,
          module: drugs.all[drug].module,
          name: drugs.all[drug].name
        })
      }
    }

    const planToSubmit: INewPlan = {
      description: formPlan.description,
      drugs: formattedDrugs,
      type: formPlan.type,
      name: formPlan.name
    }

    const { status, message } = await createPlan(planToSubmit)

    if (status >= 400) {
      updateError(message)
      return
    }

    history.push(PLANS_URL)
  }

  const handlePlanTypeSelect = (item: IDropdownItem) => {
    update({ ...formPlan, type: item.value as TPlanType })
    setFieldsValidity({
      ...fieldsValidity,
      type: {
        touched: true,
        valid: true
      }
    })
  }

  const formValid = (Object.keys(fieldsValidity) as TKey[]).every(
    (field) => fieldsValidity[field].valid && fieldsValidity[field].touched
  )

  return (
    <FormPageLayout title='Create New Plan' handleBackClick={() => history.push(PLANS_URL)}>
      <StyledForm handleSubmit={handleSubmit}>
        {error}
        <Row>
          <Col width={6}>
            <TextInputOld
              className='w-100'
              name='name'
              type='text'
              defaultValue={formPlan.name}
              onBlur={(value, valid) => handleInput('name', value, valid)}
              placeholder='Name'
              label='Plan Name'
              isRequired
            />
          </Col>
          <Col width={6}>
            <Dropdown
              fieldState='valid'
              label='Plan Type'
              showSearchThreshold={0}
              value={formPlan.type}
              placeholder='Select Plan Type'
              data={[{ value: doseMe }, { value: trial }, { value: research }, { value: tdi }, { value: customer }]}
              onSelect={handlePlanTypeSelect}
            />
          </Col>
        </Row>
        <br />

        <Row>
          <Col width={12}>
            <TextInputOld
              name='description'
              type='text'
              defaultValue={''}
              onBlur={(value, valid) => handleInput('description', value, valid)}
              placeholder='Description'
              label='Description'
            />
          </Col>
        </Row>
        <br />

        <Row>
          <Col>
            <EntitlementsInput
              data={Object.values(drugs.all)}
              selectedDrugs={formPlan.drugs}
              setSelectedDrugs={setSelectedDrugs}
            />
          </Col>
          <Col></Col>
        </Row>
        <br />
        <hr />

        <div className='d-flex w-100 justify-content-end'>
          <Button type='submit' variant='primary' onClick={noop} disabled={!formValid} loading={plansLoading}>
            Create
          </Button>
        </div>
      </StyledForm>
    </FormPageLayout>
  )
}

export { Create }
